<script lang="ts" setup>
  import type { ProductFlexibleResource, ProductVariantSimpleResource, ProductFlexibleExtraResource, ProductVariantPickerResource } from '@storefront/resources';
  import { usePersistentFormContext, useShared, useHttpClient, useRoute, useTranslator } from '@othercommerce/foundation';
  import { computed, ref, type Ref, watch, nextTick, onMounted } from 'vue';
  import { useUnitFormatter } from '@othercommerce/domain';

  interface AttributeOption {
    label: string,
    value: number,
  }

  interface Attribute {
    label: string,
    options: AttributeOption[],
    value: number,
  }

  interface Props {
    product: ProductFlexibleResource,
    extra: ProductFlexibleExtraResource,
    index: number,
  }

  const props = defineProps<Props>();
  const route = useRoute();
  const http = useHttpClient();
  const shared = useShared();
  const translator = useTranslator();
  const unitFormatter = useUnitFormatter();

  const { data } = usePersistentFormContext();

  const variant = ref() as Ref<ProductVariantPickerResource>;
  const loading = ref(true);
  const attributes = ref() as Ref<Attribute[]>;
  const selected = ref() as Ref<any>;
  const variants = ref() as Ref<ProductVariantSimpleResource[]>;

  const options = computed(() => props.extra.products.map((item) => {
    return {
      label: item.title,
      value: item.productId,
    };
  }));

  const item = computed(() => {
    return props.extra.products.find((item) => {
      return item.productId === data.value.extras[props.index].productId;
    })!;
  });

  const allowed = computed(() => {
    return props.extra.variants[item.value.productId] ?? [];
  });

  const price = computed(() => {
    if (shared.value.includeVat) {
      return item.value.priceGross;
    } else {
      return item.value.priceNet;
    }
  });

  const unit = computed(() => {
    let stockUnit = item.value.stockUnit;
    let stockBase = item.value.stockUnitBase;

    if (stockBase === 1.0) {
      return unitFormatter.symbol(stockUnit);
    }

    return unitFormatter.short(stockUnit, stockBase);
  });

  const receivable = computed(() => {
    let stockUnit = item.value.stockUnit;
    let stockBase = item.value.stockUnitBase;

    return unitFormatter.accusative(stockUnit, stockBase * props.extra.quantity);
  });

  const message = computed(() => {
    if (shared.value.includeVat) {
      return translator.trans('SHOP.COMMON.LABEL.BRUTTO_PER_UNIT', { unit: unit.value });
    } else {
      return translator.trans('SHOP.COMMON.LABEL.NETTO_PER_UNIT', { unit: unit.value });
    }
  });

  function onSelected(variant: ProductVariantSimpleResource) {
    data.value.extras[props.index].variantId = variant.id;
  }

  function reloadVariants() {
    let params = {
      product: item.value.productId,
      // get variant id from currently chosen item
      variant: item.value.variantId,
      allowed: allowed.value.length > 0 ? allowed.value : undefined,
    };

    http.dispatch('GET', route('web.product.picker.api', params)).then((response) => {
      variant.value = response.variant;
      attributes.value = response.attributes;
      selected.value = response.selected;
      variants.value = response.variants;

      nextTick(() => {
        loading.value = false;
      });
    });
  }

  watch(() => data.value.extras[props.index].productId, () => {
    reloadVariants();
  });

  watch(variant, (variant) => {
    if (variant) {
      data.value.extras[props.index].stockAvailable = variant.stockAvailable;
      data.value.extras[props.index].stockOnOrder = variant.stockOnOrder;
      data.value.extras[props.index].variantId = variant.id;
    }
  }, {
    immediate: true,
  });

  onMounted(() => {
    if (data.value.extras[props.index].productId) {
      reloadVariants();
    }
  });
</script>
<template>
  <div class="flexible-extra">
    <h3 class="heading" v-html="$t('SHOP.PRODUCT.HEADER.YOU_WILL_GET_PRODUCT_QUANTITY', { quantity: receivable })" />
    <div class="item">
      <div class="item-product">
        <SfFormSelect :floating="false" :label="$t('SHOP.PRODUCT.ACTION.CHOOSE_PRODUCT')" :name="`extras[${index}][productId]`" :options="options" />
      </div>
      <template v-if="!loading">
        <div v-if="attributes.length > 0 || variants.length > 0 " class="item-variant">
          <SfVariantPickerAttributes v-if="attributes.length > 0" :attributes :floating-labels="false" :selected :variant @selected="onSelected" />
          <SfVariantPickerGrid v-else-if="variants.length > 0" :variant :variants show="variants" @selected="onSelected" />
        </div>
      </template>
      <div class="quantity-price-wrapper">
        <div class="item-quantity">
          <SfFormInput :floating="false" :label="$t('SHOP.COMMON.LABEL.QUANTITY')" :name="`extras[${index}][quantity]`" readonly />
        </div>
        <div class="item-price">
          <SfMoney :value="price" class="price" />
          <div class="per-unit" v-html="message" />
        </div>
      </div>
    </div>
    <div v-if="extra.isDropShipping" class="drop-shipping">
      <div class="icon">
        <SfIconQuestionCircle aria-hidden="true" />
      </div>
      <div class="message">
        {{ $t('SHOP.PRODUCT.MESSAGE.DIRECTLY_FROM_MANUFACTURER') }}
      </div>
    </div>
    <Transition name="fade">
      <div v-if="!item.isDropShipping && variant && Number(variant.stockAvailable) < Number(data.extras[index].quantity)" class="unavailable">
        <div class="icon">
          <SfIconError aria-hidden="true" />
        </div>
        <span class="message">
          <template v-if="variant.stockAvailable < variant.stockUnitBase">
            {{ $t('SHOP.PRODUCT.MESSAGE.PRODUCT_NOT_AVAILABLE') }}
          </template>
          <template v-else>
            {{ $t('SHOP.PRODUCT.MESSAGE.PRODUCT_OUT_OF_STOCK') }}
          </template>
        </span>
      </div>
    </Transition>
  </div>
</template>
<style lang="scss" scoped>
  @use '@storefront/styles/mixins/media';

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity var(--transition-primary-duration) var(--transition-primary-timing-function);
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }


  .flexible-extra {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    padding: 0.75rem 1rem;
    border-radius: var(--border-radius);
    background-color: var(--subtle-background);
    border: 1px solid var(--secondary-border);

    .heading {
      font-size: 0.875rem;
      font-weight: var(--font-weight-semi);
      margin-bottom: 0.25rem;

      :deep(b) {
        font-weight: var(--font-weight-bold);
        color: var(--primary-color);
      }
    }

    @include media.query(lg) {
      gap: 0.375rem;
      padding: 0.5rem;
    }
  }

  .drop-shipping, .unavailable {
    display: flex;
    flex-direction: row;
    align-self: flex-start;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.75rem;
    line-height: 1.5;
    margin-top: 0.25rem;
    padding: 0.25rem 0.75rem;
    border-radius: var(--border-radius);

    .icon {
      display: flex;
      justify-content: center;
      align-items: center;
      flex: 0 0 auto;
      font-size: 0.875rem;
      height: 1.125rem;
      width: 0.875rem;
    }
  }

  .drop-shipping {
    color: var(--sf-status-color-info);
    background-color: var(--sf-status-background-info);
    border: 1px solid var(--sf-status-color-info);
  }

  .unavailable {
    color: var(--sf-status-color-danger);
    background-color: var(--sf-status-background-danger);
    border: 1px solid var(--sf-status-color-danger);
  }

  .item {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 0.5rem;

    .item-product {
      flex: 1 0 300px;
    }

    .item-variant {
      flex: 1 0 auto;
      max-width: 100%;

      :deep(.variant-attributes) {
        .variant-attributes__list {
          flex-direction: row;


          .control-wrapper {
            flex: 1 0 150px;
          }
        }
      }

      @include media.query(md) {
        width: 100%;
      }
    }

    .quantity-price-wrapper {
      display: flex;
      align-items: flex-end;
      justify-self: flex-end;
      flex: 0 0 auto;
      gap: 0.75rem;
      margin-left: auto;
    }

    .item-quantity {
      width: 80px;
    }

    .item-price {
      padding: 0;

      .price {
        font-weight: var(--font-weight-bold);
      }

      .per-unit {
        color: var(--secondary-color);
        font-size: var(--font-size-sm);
      }
    }
  }
</style>
